import { CONTENT_TYPES } from 'dashboard/components-next/message/constants';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { useCallsStore } from 'dashboard/stores/calls';
import types from 'dashboard/store/mutation-types';
import wootConstants from 'dashboard/constants/globals';

const { CALL_AVAILABILITIES } = wootConstants;

export const TERMINAL_STATUSES = [
  'completed',
  'busy',
  'failed',
  // Оба написания намеренно: 'no-answer' приходит от Twilio, 'no_answer' — это
  // наш собственный Call#status, и без него пропущенный звонок не гасил
  // плавающую карточку (она висела до перезагрузки страницы).
  'no-answer',
  'no_answer',
  'canceled',
  'missed',
  'ended',
];

export const isInbound = direction => direction === 'inbound';

// Providers whose audio never runs through the Twilio/WhatsApp FloatingCallWidget:
// UIS and МегаФон ВАТС ring in the vivaPhone dock (browser SIP), MANGO rings on
// the agent's own device (CTI). Feeding their calls to the widget makes it fetch
// a conference token that does not exist and fail with a 500.
const isExternalAudioProvider = provider =>
  provider === 'uis' || provider === 'mango' || provider === 'megapbx';

// …but the agent still has to see who is calling. PBX calls go into their own
// store slice; DeviceCallNotice renders them as an information-only card for the
// inboxes whose audio lives on the agent's own device, and ignores the rest.
// A PBX call is worth a notice only while it is live. Anything else is over —
// note that our own terminal status is `no_answer`, which TERMINAL_STATUSES (a
// list of Twilio's spellings) does not contain, so decide on the live statuses.
const LIVE_CALL_STATUSES = ['ringing', 'in_progress'];

const trackExternalCall = (callsStore, data, status) => {
  if (!LIVE_CALL_STATUSES.includes(status)) {
    callsStore.removeExternalCall(data.callSid);
    return;
  }
  callsStore.upsertExternalCall({ ...data, status });
};

const externalCallPayload = ({
  callSid,
  callId,
  provider,
  conversationId,
  inboxId,
  callDirection,
  senderId,
  caller,
}) => ({
  callSid,
  callId,
  provider,
  conversationId,
  inboxId,
  callDirection,
  senderId,
  caller,
});

const isVoiceCallMessage = message => {
  return CONTENT_TYPES.VOICE_CALL === message?.content_type;
};

const shouldSkipCall = (callDirection, senderId, currentUserId) => {
  return callDirection === 'outbound' && senderId !== currentUserId;
};

const extractAssigneeId = conversation => {
  return conversation?.assignee_id || conversation?.meta?.assignee?.id || null;
};

const isAssignedToAnotherAgent = (assigneeId, currentUserId) => {
  if (currentUserId == null) return false;
  return !!assigneeId && assigneeId !== currentUserId;
};

const shouldShowCall = ({
  callDirection,
  senderId,
  assigneeId,
  currentUserId,
}) => {
  if (shouldSkipCall(callDirection, senderId, currentUserId)) return false;
  // Outbound calls are scoped to the initiator via shouldSkipCall; the
  // conversation may be auto-assigned to a different agent on creation, so
  // skip the assignee filter for outbound to avoid hiding the caller's own widget.
  if (callDirection === 'outbound') return true;
  return !isAssignedToAnotherAgent(assigneeId, currentUserId);
};

// Agents who are not taking calls shouldn't get a ringing popup for inbound
// calls, but outbound calls always belong to the initiator regardless of their
// status, and existing (already-surfaced) calls keep going so a status change
// mid-call doesn't yank away an active widget.
// This must stay in step with the SIP-registration gate in VivaPhoneDock: an
// agent whose line rings but whose popup never opens has no way to answer.
const shouldRingInbound = (callDirection, currentUserAvailability) => {
  if (callDirection === 'outbound') return true;
  return CALL_AVAILABILITIES.includes(currentUserAvailability);
};

function extractCallerSnapshot(message) {
  // Snapshot caller info from the message at add-time so the widget can keep
  // rendering it after the user navigates away from a conversation list that
  // had the conversation hydrated (and Vuex evicts it from the store).
  // Only incoming messages carry the contact as the sender; on outbound calls
  // the sender is the initiating agent, so skip the snapshot and let the widget
  // fall back to the conversation's contact (conversation.meta.sender).
  if (message?.message_type !== MESSAGE_TYPE.INCOMING) return null;
  const sender = message?.sender;
  if (!sender) return null;
  return {
    name: sender.name,
    phone: sender.phone_number,
    avatar: sender.avatar || sender.thumbnail,
    additionalAttributes: sender.additional_attributes || {},
  };
}

function extractCallData(message) {
  const call = message?.call || {};
  return {
    callSid: call.provider_call_id,
    callId: call.id,
    provider: call.provider,
    status: call.status,
    callDirection: call.direction === 'outgoing' ? 'outbound' : 'inbound',
    conversationId: message?.conversation_id,
    inboxId: message?.inbox_id ?? message?.conversation?.inbox_id,
    assigneeId: extractAssigneeId(message?.conversation),
    senderId: message?.sender?.id,
    caller: extractCallerSnapshot(message),
  };
}

export function handleVoiceCallCreated(
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const callData = extractCallData(message);
  const {
    callSid,
    callId,
    provider,
    callDirection,
    conversationId,
    inboxId,
    assigneeId,
    senderId,
  } = callData;

  // Звонок АТС: сам звонок в этом сторе не живёт (аудио либо в доке, либо на
  // телефоне оператора) — нужна только карточка «кто звонит». Она
  // информационная, поэтому её НЕ гасят ни назначение диалога на коллегу, ни
  // статус: на устройства АТС звонит всем сразу, и человек с звонящим телефоном
  // должен видеть номер, даже если диалог автораспределение отдало другому.
  // Чужой исходящий по-прежнему не показываем — он принадлежит тому, кто набрал.
  if (isExternalAudioProvider(provider)) {
    if (shouldSkipCall(callDirection, senderId, currentUserId)) return;

    trackExternalCall(
      useCallsStore(),
      externalCallPayload(callData),
      'ringing'
    );
    return;
  }

  if (
    !shouldShowCall({
      callDirection,
      senderId,
      assigneeId,
      currentUserId,
    })
  ) {
    return;
  }

  if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

  const callsStore = useCallsStore();
  callsStore.addCall({
    callSid,
    callId,
    provider,
    conversationId,
    inboxId,
    callDirection,
    senderId,
    caller: extractCallerSnapshot(message),
  });
}

export function handleVoiceCallUpdated(
  commit,
  message,
  currentUserId,
  currentUserAvailability
) {
  if (!isVoiceCallMessage(message)) return;

  const callData = extractCallData(message);
  const {
    callSid,
    callId,
    provider,
    status,
    callDirection,
    conversationId,
    inboxId,
    assigneeId,
    senderId,
  } = callData;

  const callsStore = useCallsStore();

  callsStore.handleCallStatusChanged({ callSid, status, conversationId });

  commit(types.UPDATE_MESSAGE_CALL_STATUS, {
    conversationId,
    callStatus: status,
    callSid,
  });

  // Звонок АТС: карточка «кто звонит» живёт своей жизнью — см. комментарий в
  // handleVoiceCallCreated. Здесь она только следует за статусом и исчезает,
  // когда звонок кончился.
  if (isExternalAudioProvider(provider)) {
    if (shouldSkipCall(callDirection, senderId, currentUserId)) return;

    trackExternalCall(callsStore, externalCallPayload(callData), status);
    return;
  }

  if (
    !shouldShowCall({
      callDirection,
      senderId,
      assigneeId,
      currentUserId,
    })
  ) {
    callsStore.removeCall(callSid);
    return;
  }

  if (status === 'ringing') {
    if (!shouldRingInbound(callDirection, currentUserAvailability)) return;

    callsStore.addCall({
      callSid,
      callId,
      provider,
      conversationId,
      inboxId,
      callDirection,
      senderId,
      caller: extractCallerSnapshot(message),
    });
  }
}

export function syncConversationCallVisibility(conversation, currentUserId) {
  const assigneeId = extractAssigneeId(conversation);
  if (!isAssignedToAnotherAgent(assigneeId, currentUserId)) return;

  // Outbound calls belong to the initiator regardless of who the conversation
  // is currently assigned to (auto-assignment may flip mid-call). Mirror
  // shouldShowCall's outbound exception so an in-progress outbound call isn't
  // ripped out from under the caller when the conversation reassigns.
  const callsStore = useCallsStore();
  const callsToRemove = callsStore.calls.filter(
    call =>
      call.conversationId === conversation.id &&
      !shouldShowCall({
        callDirection: call.callDirection,
        senderId: call.senderId,
        assigneeId,
        currentUserId,
      })
  );
  callsToRemove.forEach(call => callsStore.removeCall(call.callSid));
}
